import { useState } from "react";
import { Film, Upload, ChevronDown, ShieldCheck } from "lucide-react";
import { guideFor, SCREEN_RECORDING } from "@/lib/downloadGuides";

const Steps = ({ items, testid }) => (
  <ol className="space-y-1.5 text-xs text-neutral-300" data-testid={testid}>
    {items.map((s, i) => <li key={s} className="flex gap-2"><span className="text-sky-300 font-mono w-4 shrink-0">{i + 1}.</span><span>{s}</span></li>)}
  </ol>
);

function DeviceSteps({ g }) {
  const [tab, setTab] = useState("phone");
  if (!g.phone) return g.steps ? <Steps items={g.steps} testid="guide-steps" /> : null;
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {["phone", "computer"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`guide-tab-${t}`}
            className={`text-[11px] px-2.5 py-1 rounded-full capitalize transition-colors ${tab === t ? "bg-sky-400 text-white" : "text-neutral-400 hover:text-white"}`}>{t}</button>
        ))}
      </div>
      <Steps items={g[tab]} testid="guide-steps" />
    </div>
  );
}

function ScreenRecording() {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} data-testid="guide-screen-recording-toggle" className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1 transition-colors">
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} /> No download option? Record your screen
      </button>
      {open && (
        <div className="mt-2 grid sm:grid-cols-2 gap-3" data-testid="guide-screen-recording">
          {Object.entries(SCREEN_RECORDING).map(([device, steps]) => (
            <div key={device}><p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">{device}</p><Steps items={steps} /></div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DownloadGuide({ guide, onClose }) {
  if (!guide) return null;
  const g = guideFor(guide.url);
  return (
    <div className="mb-3 rounded-2xl border border-white/10 bg-[#121725] p-4 space-y-3 max-h-[55vh] overflow-y-auto scroll-thin rise" data-testid="video-site-guide">
      <div className="flex gap-2 items-start">
        <Film className="h-4 w-4 text-sky-300 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-medium text-neutral-100">Get this video from {g.site}</p>
          {guide.message && <p className="text-xs text-neutral-400 mt-0.5">{guide.message}</p>}
        </div>
      </div>
      {guide.notPublic && g.share && <div><p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Make the link public</p><Steps items={g.share} testid="guide-share-steps" /></div>}
      <DeviceSteps g={g} />
      <ScreenRecording />
      <p className="text-[11px] text-neutral-500 flex gap-1.5"><ShieldCheck className="h-3 w-3 shrink-0 mt-0.5" /> Only download videos you're allowed to: your own recordings, class recordings shared with you, or videos whose owner allows downloads.</p>
      <div className="flex gap-2">
        <button onClick={() => { onClose(); window.dispatchEvent(new Event("video-upload-pick")); }} data-testid="video-guide-upload-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-sky-400 text-white hover:bg-sky-500 transition-colors">
          <Upload className="h-3 w-3" /> Upload the file
        </button>
        <button onClick={onClose} data-testid="video-guide-dismiss-btn" className="text-xs px-3 py-1.5 rounded-full text-neutral-400 hover:text-white transition-colors">Dismiss</button>
      </div>
    </div>
  );
}
